/**
 * Sprint T-17.1 · Notification Center · shared dialogs.
 * Retry DLQ · Discard DLQ · Delete webhook endpoint · Global unsubscribe confirm.
 * Reuses the ModalShell pattern from T-16.1 (rewritten to avoid cross-import).
 */
import React from "react";
import { X, RefreshCw, Trash2, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";

// ============================================================
// Base modal shell (dumb · shared)
// ============================================================

export function ModalShell({ title, onClose, children, testId, footer, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : size === "sm" ? "max-w-sm" : "max-w-lg";
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className={`max-h-[85vh] w-full ${width} overflow-hidden rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-[var(--ag-border)] px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--ag-text-dim)]">
            {title}
          </span>
          <button
            type="button"
            data-testid={`${testId}-close`}
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] p-1 hover:bg-[var(--ag-surface-2)]"
            aria-label="Cerrar"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto p-4">{children}</div>
        {footer && <footer className="border-t border-[var(--ag-border)] px-3 py-2">{footer}</footer>}
      </div>
    </div>
  );
}

// ============================================================
// DLQ Retry
// ============================================================

export function DlqRetryDialog({ entry, onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title={`Reintentar DLQ · ${entry.template_id}`}
      onClose={onClose}
      testId="notif-dlq-retry-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-dlq-retry-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="notif-dlq-retry-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded bg-emerald-500 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            <RefreshCw className="h-3 w-3" /> {pending ? "Enviando…" : "Reintentar"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <p>Se re-encolará el intent · el sweep lo procesará de nuevo desde attempt=0.</p>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Canal · destino</div>
          <div className="font-mono text-xs">{entry.channel} → {entry.to}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Intentos previos</div>
          <div className="font-mono">{entry.attempts}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Último error</div>
          <div className="rounded border border-red-500/30 bg-red-500/5 p-2 text-[10px]">
            <code className="font-mono text-red-500">{entry.last_error?.type || "—"}</code>
            <span className="ml-2">{(entry.last_error?.message || "").slice(0, 240)}</span>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================
// DLQ Discard
// ============================================================

export function DlqDiscardDialog({ entry, onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title={`Descartar DLQ · ${entry.template_id}`}
      onClose={onClose}
      testId="notif-dlq-discard-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-dlq-discard-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="notif-dlq-discard-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-500 hover:bg-red-500/20 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" /> {pending ? "Enviando…" : "Descartar"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <p>La entrada quedará marcada como descartada en el audit log T-14.</p>
        <p className="text-[var(--ag-text-dim)]">
          Esta acción no es reversible desde la UI, pero el registro permanece consultable en el
          histórico del dispatcher.
        </p>
      </div>
    </ModalShell>
  );
}

// ============================================================
// Webhook Endpoint · Delete
// ============================================================

export function DeleteEndpointDialog({ endpoint, onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title={`Eliminar endpoint · ${endpoint.name}`}
      onClose={onClose}
      testId="notif-delete-endpoint-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-delete-endpoint-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="notif-delete-endpoint-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-500 hover:bg-red-500/20 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" /> {pending ? "Enviando…" : "Eliminar"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-amber-500">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Intents con este endpoint fallarán con `webhook endpoint not found`.
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">URL</div>
          <div className="font-mono text-xs break-all">{endpoint.url}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Tenant</div>
          <div className="font-mono text-xs">{endpoint.tenant_id || "global"}</div>
        </div>
      </div>
    </ModalShell>
  );
}

// ============================================================
// Signing Secret · one-time reveal
// ============================================================

export function SigningSecretDialog({ endpoint, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(endpoint.signing_secret);
      toast.success("Signing secret copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar. Selecciona y copia manualmente.");
    }
  };
  return (
    <ModalShell
      title="Signing Secret · guárdalo AHORA"
      onClose={onClose}
      testId="notif-signing-secret-dialog"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-signing-secret-copy"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            <Copy className="h-3 w-3" /> Copiar
          </button>
          <button
            type="button"
            data-testid="notif-signing-secret-done"
            onClick={onClose}
            className="rounded bg-[var(--ag-gold)] px-3 py-1 text-xs font-medium text-black"
          >
            Ya lo guardé
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-[11px]">
        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-amber-500">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Este secret solo se muestra una vez · guárdalo en tu gestor de credenciales.
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Endpoint</div>
          <div className="font-mono text-xs">{endpoint.name} → {endpoint.url}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Signing secret (HMAC-SHA256)</div>
          <pre
            data-testid="notif-signing-secret-value"
            className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)] p-2 font-mono text-[11px] break-all whitespace-pre-wrap"
          >
            {endpoint.signing_secret}
          </pre>
        </div>
        <p className="text-[var(--ag-text-dim)]">
          Verifica la firma en el receptor con:
          <br />
          <code className="font-mono">HMAC-SHA256(secret, `${'{ts}'}.${'{body}'}`)</code>
        </p>
      </div>
    </ModalShell>
  );
}

// ============================================================
// Unsubscribe (destructive confirm)
// ============================================================

export function UnsubscribeConfirmDialog({ onClose, onConfirm, pending }) {
  return (
    <ModalShell
      title="Cancelar todas las notificaciones"
      onClose={onClose}
      testId="notif-unsub-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="notif-unsub-cancel"
            onClick={onClose}
            className="rounded border border-[var(--ag-border)] px-3 py-1 text-xs hover:bg-[var(--ag-surface-2)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="notif-unsub-confirm"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs text-red-500 hover:bg-red-500/20 disabled:opacity-50"
          >
            <AlertTriangle className="h-3 w-3" /> {pending ? "Enviando…" : "Sí, cancelar suscripción"}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-[11px]">
        <p>Marcarás tu cuenta como <strong>unsubscribed_globally</strong>.</p>
        <p className="text-[var(--ag-text-dim)]">
          Seguirás recibiendo notificaciones <strong>transaccionales</strong> obligatorias
          (verificación de cuenta, reset de contraseña, alertas de seguridad).
        </p>
      </div>
    </ModalShell>
  );
}
